// Core packages
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';

// Third party packages
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from "rxjs";
import { finalize } from 'rxjs/operators';

// Custom packages
import { AuthService } from 'src/app/modules/auth/auth.service';
import { PropertiesService } from 'src/app/modules/properties/properties.service';
import { UsersService } from "src/app/modules/users/users.service";
import { IUser } from 'src/app/shared/models/user/user.interface';
import { ApiService } from "src/app/shared/services/api.service";
import { ConfigService } from 'src/app/shared/services/config.service';
import { HelperService } from 'src/app/shared/services/helper.service';

interface Tagger {
  _id: string;
  completeName: string;
}

@Component({
  selector: 'app-assign-taggers-dialog',
  templateUrl: './assign-taggers-dialog.component.html',
  styleUrls: ['./assign-taggers-dialog.component.scss']
})
export class AssignTaggersDialogComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  lang = this.translateService.currentLang || this.translateService.defaultLang;

  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly

  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant('common.cta.add'),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: 'primary',
    spinnerColor: 'primary',
    fullWidth: false,
    disabled: false,
    mode: 'indeterminate',
    buttonIcon: {
      fontSet: 'fal',
      fontIcon: 'fa-save',
      inline: true,
    },
  };
  // END - form
  
  dialogIsLoading = true;
  initTaggers: Tagger[] = [];

  constructor(
    public dialogRef: MatDialogRef<AssignTaggersDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,

    public configService: ConfigService,
    private cookieService: CookieService,
    private translateService: TranslateService,
    private propertiesService: PropertiesService,
    private authService: AuthService,
    private helperService: HelperService,
    private toastrService: ToastrService,
    private usersService: UsersService,
    private apiService: ApiService,
  ) {
    dialogRef.disableClose = true;
  }

  /**
   * Init component
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Get logged user data
    this.subscriptions.push(this.authService.loggedUser$.subscribe(res => this.loggedUser = res));

    // Get current lang
    this.lang = this.cookieService.get('lang') || this.configService.defaultLang;

    this.subscriptions.push(
      this.usersService.getTaggerList(0, 40, 'firstName').subscribe(
        (res: any) => {
          if (res.data.items && Array(res.data.items)) {
            for  (const item of res.data.items) {
              this.initTaggers.push({
                _id: item._id,
                completeName: item.completeName
              });
            }
          }
          // Init add form
          this.initForm();
        },
        err => {
          // Init add form
          this.initForm();
        }
      )
    )

    // Stop loading dialog
    this.dialogIsLoading = false;
  }
  
  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(): void {
    // Set model
    this.model = {};

    // Set fields
    this.fields = [
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            className: "col-12",
            type: "autocomplete",
            key: "tagger",
            templateOptions: {
              required: false,
              customLabel: this.translateService.instant(
                "properties.dialog.assignTagger.searchTagger"
              ),
              placeholder: this.translateService.instant(
                "properties.dialog.assignTagger.searchTagger"
              ),
              initialValues: this.initTaggers,
              bindLabel: "completeName",
              bindValue: "_id",
              search$: this.usersService.getTaggerList.bind(this),
              typeToSearchText: this.translateService.instant(
                "common.words.typeToSearch",
                { minTermLength: 1 }
              ),
              clearAllText: this.translateService.instant(
                "common.words.clearAll"
              ),
              notFoundText: this.translateService.instant(
                "common.words.notFound"
              ),
              loadingText: this.translateService.instant(
                "common.words.loading"
              ),
            },
            // expressionProperties: {
            //   "templateOptions.disabled": (model: any) => {
            //     return model.payingMaster || model.payingSupplier || model.supplier;
            //   },
            // },
          },
        ],
      },
    ];
  }

  /**
   * Handle dialog closing
   *
   * @since 1.0.0
   *
   * @param [data] optional data passed to the caller component
   */
  onClose(data?: any): void {
    this.dialogRef.close(data);
  }

  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
  onFormSubmit(model: any): void {
    // Prevent invalid form submit
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        this.form.controls[key].markAsTouched();
      });
      return;
    }

    const userId = model.tagger;

    const data = {
      properties: []
    }

    if (this.data.properties && Array.isArray(this.data.properties)) {
      data.properties = this.data.properties;
    }

    this.subscriptions.push(
      this.propertiesService.assignTaggers(userId, data)
        .pipe(
          finalize(() => this.formBtnOptions.active = false)
        )
        .subscribe(
          res => {
            if (res.status) {
              this.toastrService.success(res.message);
              this.onClose({ updated: true });
              return;
            }
            // this.toastrService.error(this.translateService.instant('common.errors.unknown_server_error'));
          },
          err => {
            // Choose one of the following error handling method.
            // 1) First one show a message right under the form fields
            //    (if the form is properly setted)
            // 2) Second one is used to show a mat-error under each field
            //    in the dynamic form (is meant to be used only in dynamicForm)
            // 3) Third one show toastr notifications for each error
            // 4) Fourth one is used to show mat-error under each field
            //    in the ngx-formly form (is mean to be used only with ngx-formly)
            // this.helperService.handleFormError(form, err);
            // this.helperService.handleDynamicFormError(form, err);
            // this.helperService.handleError(err);
            this.helperService.handleFormlyError(this.form, err);
          }
        )
    );
  }
}
